import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Box, 
  Paper, 
  Grid, 
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ContactsIcon from '@mui/icons-material/Contacts';
import PetsIcon from '@mui/icons-material/Pets';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const LandingPage = ({ onStaffLoginClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  const handlePrivacyOpen = () => {
    setPrivacyDialogOpen(true);
  };

  const handlePrivacyClose = () => {
    setPrivacyDialogOpen(false);
  };

  const handleTermsOpen = () => {
    setTermsDialogOpen(true);
  };

  const handleTermsClose = () => {
    setTermsDialogOpen(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(to bottom, #f8f9fa, #e9ecef)'
    }}>
      {/* Header with logo on white background */}
      <Box sx={{ 
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        zIndex: 1
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 2,
            position: 'relative'
          }}>
            <Box sx={{ width: '33%' }}>
              <img src="/logo.png" alt="Zoomin Groomin Logo" style={{ height: '60px' }} />
            </Box>
            
            <Box sx={{ width: '33%', textAlign: 'center' }}>
              <Typography 
                variant="h3" 
                component="h1" 
                fontWeight="bold"
                sx={{ 
                  color: '#1976d2',
                  fontSize: '2.3rem',
                  letterSpacing: '0.5px',
                  textShadow: '0px 1px 1px rgba(0,0,0,0.1)'
                }}
              >
                Zoomin Groomin
              </Typography>
            </Box>
            
            <Box sx={{ width: '33%', display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                onClick={onStaffLoginClick}
                sx={{ 
                  fontWeight: 'bold',
                  mr: 0
                }}
              >
                Staff Portal Login
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
        pt: 8,
        pb: 6,
        color: 'white',
        textAlign: 'center',
        borderBottom: '5px solid #1565c0'
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 4, 
              fontWeight: 400,
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
              fontSize: isMobile ? '1.5rem' : '2.2rem'
            }}
          >
            Professional Mobile Pet Grooming Services
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 4, 
              width: isMobile ? '90%' : '800px', 
              mx: 'auto', 
              fontWeight: 300,
              whiteSpace: 'nowrap'
            }}
          >
            Bringing exceptional grooming directly to your doorstep in Greater Tampa Bay
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 500,
              color: 'white',
              textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
              mb: 2
            }}
          >
            Call Us To Schedule Your Pet at (727) 877-2776
          </Typography>
        </Container>
      </Box>
      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" align="center" fontWeight="bold" gutterBottom>
          Why Choose Zoomin Groomin?
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Professional grooming services with convenience and care for your pets
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <PetsIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Stress-Free Experience
                </Typography>
                <Typography>
                  Our mobile service eliminates travel anxiety for your pets. They enjoy professional grooming in the comfort of familiar surroundings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Experienced Groomers
                </Typography>
                <Typography>
                  Our professional groomers are experienced and passionate about pets. We provide personalized care for your pet.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <BusinessIcon sx={{ fontSize: 60, color: '#1976d2', mb: 2 }} />
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Fully-Equipped Vans
                </Typography>
                <Typography>
                  Our state-of-the-art mobile grooming vans come with everything needed for a complete grooming experience, right at your doorstep.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      
      {/* About Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center'
              }}>
                <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                  About Grimes Grooming, LLC dba Zoomin Groomin
                </Typography>
                <Typography variant="body1" paragraph>
                  Zoomin Groomin of Greater Tampa Bay is a proud franchisee of the Zoomin Groomin family of businesses. This business is independently owned and operated by <strong>Grimes Grooming LLC.</strong>
                  We provide high-quality mobile pet grooming services across the Greater Tampa Bay region, inclusive of the following counties: Hillsborough, Polk, Pinellas, Pasco, Manatee, and Sarasota.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our team of experienced groomers provides exceptional grooming services that caters to your pets comfort and safety in our specially equipped vans that comes directly to you. We use this site to manage our operations, coordinate appointments, and support our grooming staff.
                </Typography>
                <Typography variant="body1" paragraph>
                  Founded in 2024, we have grown to serve thousands of clients across six counties in the Greater Tampa Bay area, always maintaining our commitment to quality, compassion, and convenience.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Button variant="outlined" color="primary" href="#contact">
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card 
                elevation={5}
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  transform: 'rotate(2deg)',
                  transition: '0.3s',
                  '&:hover': {
                    transform: 'rotate(0deg) scale(1.02)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  alt="CALL US AT (727) 877-2776"
                  height="400"
                  image="https://placehold.co/600x400/1976d2/FFFFFF.png?text=GIVE+YOUR+PET+THE+EXPERIENCE+THEY+DESERVE"
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Information */}
      <Container id="contact" maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" align="center" fontWeight="bold" gutterBottom>
          Contact Us
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Have questions? We're here to help you and your pet
        </Typography>
        
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4,
            borderRadius: 2,
            background: 'linear-gradient(to right, #f5f7fa, #ffffff)'
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <ContactsIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    Customer Service
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Our friendly team is here to help with any questions about our services.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    <strong>Phone:</strong> (727) 877-2776
                  </Typography>
                  <Typography variant="body1">
                    <strong>Email:</strong> zoomingroomin@zggtb.com
                  </Typography>
                  <Typography variant="body1">
                    <strong>Hours:</strong> Sunday - Saturday, 9am - 7pm EST
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <BusinessIcon color="primary" sx={{ fontSize: 30, mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    Corporate Office
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Our main office location for corporate and partnership inquiries.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    <strong>Grimes Grooming, LLC dba Zoomin Groomin</strong><br />
                    13716 Kennedy Drive<br />
                    North Redington Beach, FL 33708<br />
                    United States
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      
      {/* Footer */}
      <Box 
        sx={{ 
          bgcolor: '#1976d2', 
          color: 'white',
          py: 4,
          mt: 'auto'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img src="/logo.png" alt="Zoomin Groomin Logo" style={{ height: '40px' }} />
                <Typography variant="h6" sx={{ ml: 1 }}>
                  Zoomin Groomin
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Professional mobile pet grooming services bringing exceptional care directly to your doorstep.
              </Typography>
              <Typography variant="body2">
                Greater Tampa Bay
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Quick Links
              </Typography>
              <Link href="#" color="inherit" sx={{ display: 'block', mb: 1 }}>Home</Link>
              <Link href="#contact" color="inherit" sx={{ display: 'block', mb: 1 }}>Contact</Link>
              <Link 
                component="button" 
                onClick={handlePrivacyOpen} 
                color="inherit" 
                sx={{ display: 'block', mb: 1 }}
              >
                Privacy Policy
              </Link>
              <Link 
                component="button" 
                onClick={handleTermsOpen} 
                color="inherit" 
                sx={{ display: 'block', mb: 1 }}
              >
                Terms of Service
              </Link>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Connect With Us
              </Typography>
              <Typography variant="body2" paragraph>
                Follow us on social media for tips, updates, and adorable pet transformations!
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link href="https://facebook.com/ZoominGroominGreaterTampaBayArea" target="_blank" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
                  Facebook: facebook.com/ZoominGroominGreaterTampaBayArea
                </Link>
                <Link href="https://instagram.com/zoomingroomingtb" target="_blank" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
                  Instagram: @zoomingroomingtb
                </Link>
              </Box>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Grimes Grooming LLC dba Zoomin Groomin. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Privacy Policy Dialog */}
      <Dialog
        open={privacyDialogOpen}
        onClose={handlePrivacyClose}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrivacyTipIcon color="primary" sx={{ fontSize: 24, mr: 2 }} />
            <Typography variant="h5">Privacy Policy</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>Last Updated: May 2025</Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            1. Information We Collect
          </Typography>
          <Typography paragraph>
            Zoomin Groomin collects information necessary to provide our services, including contact details, pet information, appointment preferences, and payment information. 
            We may also collect usage data from our website and applications to improve our services.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            2. How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use collected information to: provide and improve our grooming services; communicate about appointments, promotions, and service updates; 
            process payments; and analyze usage patterns to enhance user experience.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            3. Information Sharing
          </Typography>
          <Typography paragraph>
            We do not sell your personal information to third parties. We share information only with service providers who assist in our operations, 
            or when required by law. Our employees access your information only as necessary to perform their job functions.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            4. Your Rights and Choices
          </Typography>
          <Typography paragraph>
            You have the right to access, correct, or delete your personal information. You may opt out of marketing communications at any time. 
            To exercise these rights, please contact us at zoomingroomin@zggtb.com.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            5. Data Security
          </Typography>
          <Typography paragraph>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, 
            alteration, disclosure, or destruction.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            6. Children's Privacy
          </Typography>
          <Typography paragraph>
            Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            7. Changes to This Policy
          </Typography>
          <Typography paragraph>
            We may update this policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes 
            via email or through our website.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            8. Contact Us
          </Typography>
          <Typography paragraph>
            If you have questions about this Privacy Policy, please contact us at zoomingroomin@zggtb.com or call us at (727) 877-2776.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePrivacyClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Terms of Service Dialog */}
      <Dialog
        open={termsDialogOpen}
        onClose={handleTermsClose}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GavelIcon color="primary" sx={{ fontSize: 24, mr: 2 }} />
            <Typography variant="h5">Terms of Service</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" gutterBottom>Last Updated: May 2025</Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            1. Service Agreement
          </Typography>
          <Typography paragraph>
            By using Zoomin Groomin's services, you agree to these Terms of Service. Our services include mobile pet grooming, 
            appointment scheduling, and related activities provided by Zoomin Groomin and its affiliates.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            2. Appointment Policy
          </Typography>
          <Typography paragraph>
            We require 24-hour notice for appointment cancellations or rescheduling. Late cancellations or no-shows may incur a fee. 
            We reserve the right to refuse service based on pet behavior, health concerns, or other factors affecting service safety.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            3. Pet Health and Safety
          </Typography>
          <Typography paragraph>
            You agree to disclose any health issues, behavioral concerns, or special needs that may affect grooming services. 
            We prioritize pet safety and may recommend veterinary care when appropriate.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            4. Payment Terms
          </Typography>
          <Typography paragraph>
            Payment is due at the time of service. We accept credit cards, cash, and approved payment methods. 
            Price adjustments may occur based on pet condition, behavior, or special requirements.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            5. Intellectual Property
          </Typography>
          <Typography paragraph>
            All content, logos, and materials on our website and applications are the property of Zoomin Groomin and protected by intellectual property laws. 
            Unauthorized use is prohibited.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            6. Limitation of Liability
          </Typography>
          <Typography paragraph>
            Zoomin Groomin is not liable for any indirect, incidental, special, or consequential damages arising from our services. 
            Our total liability shall not exceed the amount paid for the specific service in question.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            7. Dispute Resolution
          </Typography>
          <Typography paragraph>
            Any disputes shall be resolved through mediation and binding arbitration in accordance with applicable laws.
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            8. Changes to Terms
          </Typography>
          <Typography paragraph>
            We may update these terms periodically. Continued use of our services constitutes acceptance of any revised terms.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTermsClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;